/**
 * Auth Domain Schemas
 *
 * Authentication and user management schemas with inferred TypeScript types.
 * Covers login, registration, password management, and user profiles.
 */

import { z } from 'zod';

// User role enum for authorization
export const userRoleSchema = z.enum(['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER']);

// Identity provider the user authenticated with (must match backend AuthProvider enum values)
export const authProviderSchema = z.enum(['email', 'azure-ad', 'google']);

// Per-user email notification preferences
export const notificationPreferencesSchema = z.object({
  alerts: z.boolean(),
  reports: z.boolean(),
});

// Permission enum for fine-grained access control
// Must match backend Permission enum values (colon notation)
export const permissionSchema = z.enum([
  'admin',
  'view:dashboard',
  'view:tracking',
  'view:location-analytics',
  'view:reports',
  'view:assets',
  'view:tags',
  'view:cycles',
  'view:factory',
  'view:users',
  'view:settings',
]);

/**
 * User Domain Schema - Core user entity
 *
 * Represents the base user without tenant-specific data.
 * Tenant-level permissions (role, permissions, factoryAccess) are in UserTenantAccess.
 *
 * @relationship One User → Many UserTenantAccess (one per tenant)
 * @see userTenantAccessSchema for tenant-scoped permissions
 */
export const userSchema = z.object({
  // Identity
  uid: z.string(),
  userId: z.string(),
  email: z.email(),

  // Profile
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phoneNumber: z.string().optional(),
  photoURL: z.string().url().optional(),
  authProvider: authProviderSchema.optional(),
  notificationPreferences: notificationPreferencesSchema.optional(),

  // Status
  isActive: z.boolean(),
  isSuperAdmin: z.boolean().default(false),

  // Timestamps (optional - only in full user context)
  lastLoginAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

// Change password schema
// Messages are i18n keys (not raw English) — translated via mapZodErrorsToForm.
export const changePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'settings.password.errors.current_required'),
    newPassword: z.string().min(8, 'settings.password.errors.weak_password'),
    confirmNewPassword: z.string(),
  })
  .refine(data => data.newPassword === data.confirmNewPassword, {
    message: 'settings.password.errors.passwords_dont_match',
    path: ['confirmNewPassword'],
  })
  .refine(data => data.currentPassword !== data.newPassword, {
    message: 'settings.password.errors.same_as_current',
    path: ['newPassword'],
  });

// Multi-tenant schemas
// Backend returns Record<string, unknown> for tenant.settings
// Factory-specific settings include these fields (but tenant settings may vary)
export const tenantSettingsSchema = z
  .object({
    // Common factory settings
    timezone: z.string().optional(),
    alertsEnabled: z.boolean().optional(),
    positionUpdateInterval: z.number().optional(),
    maxAssets: z.number().optional(),
    workingHours: z
      .object({
        start: z.string(), // Format: "HH:MM"
        end: z.string(), // Format: "HH:MM"
      })
      .refine(data => data.end > data.start, {
        message: 'factory.validation.end_after_start',
        path: ['end'],
      })
      .optional(),
  })
  .passthrough(); // Allow additional custom settings per tenant

/**
 * Factory Access Schema
 *
 * Represents a user's access to a specific factory within a tenant.
 * Allows factory-level role and permission overrides.
 *
 * @relationship Part of UserTenantAccess.factoryAccess array
 */
export const factoryAccessSchema = z.object({
  factoryId: z.string(),
  role: userRoleSchema.optional(),
  permissions: z.array(permissionSchema).optional(),
});

export type FactoryAccess = z.infer<typeof factoryAccessSchema>;

/**
 * User Tenant Access Schema
 *
 * Represents a user's access within a specific tenant.
 * Contains tenant-scoped role, permissions, and factory access.
 *
 * @relationship Composite key: userId + tenantId
 * @see userSchema for base user data
 * @see factoryAccessSchema for factory-level permissions
 */
export const userTenantAccessSchema = z.object({
  // Composite key
  userId: z.string(),
  tenantId: z.string(),

  // Tenant info (denormalized)
  name: z.string(),
  slug: z.string(),
  domain: z.string().optional(),
  settings: tenantSettingsSchema.optional(),
  isActive: z.boolean(),

  // Timestamps
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),

  // User's role within this tenant
  role: userRoleSchema,
  permissions: z.array(permissionSchema),
  factoryAccess: z.array(factoryAccessSchema).optional(),
});

export type UserTenantAccess = z.infer<typeof userTenantAccessSchema>;

// Inferred TypeScript types - single source of truth
export type UserRole = z.infer<typeof userRoleSchema>;
export type AuthProvider = z.infer<typeof authProviderSchema>;
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;
export type Permission = z.infer<typeof permissionSchema>;
export type User = z.infer<typeof userSchema>;
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;
export type ChangePasswordForm = z.infer<typeof changePasswordFormSchema>;

// Utility constants and helpers
// These hold i18n KEYS, not display text — callers must pass them through
// t() (see users.roles.* / users.roleDescriptions.* in the locale files).
export const userRoleLabels: Record<UserRole, string> = {
  ADMIN: 'users.roles.ADMIN',
  MANAGER: 'users.roles.MANAGER',
  OPERATOR: 'users.roles.OPERATOR',
  VIEWER: 'users.roles.VIEWER',
};

export const userRoleDescriptions: Record<UserRole, string> = {
  ADMIN: 'users.roleDescriptions.ADMIN',
  MANAGER: 'users.roleDescriptions.MANAGER',
  OPERATOR: 'users.roleDescriptions.OPERATOR',
  VIEWER: 'users.roleDescriptions.VIEWER',
};
